import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Task } from '@/hooks/useTasks';

// Query keys
export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (projectId?: string) => [...taskKeys.lists(), { projectId }] as const,
};

// Storage key
const STORAGE_KEY = 'veritas_projects';

// Initial mock data
const initialTasks: Task[] = [
  // Project 1 - Tech Corp
  { id: "1", title: "Review financial statements", description: "Analyze last 3 years", status: "done", priority: "high", assignee: "Anna Lindqvist", dueDate: "2025-01-15", workstream: "Financial", projectId: "1", projectName: "Tech Corp Acquisition" },
  { id: "2", title: "Contract analysis", description: "Review all major contracts", status: "in_progress", priority: "high", assignee: "Erik Johansson", dueDate: "2025-01-20", workstream: "Legal", projectId: "1", projectName: "Tech Corp Acquisition" },
  { id: "3", title: "Tax compliance check", description: "Verify tax filings", status: "in_progress", priority: "medium", assignee: "Maria Svensson", dueDate: "2025-01-25", workstream: "Tax", projectId: "1", projectName: "Tech Corp Acquisition" },
  { id: "4", title: "Employee contracts review", description: "Check employment agreements", status: "todo", priority: "medium", assignee: "Johan Karlsson", dueDate: "2025-02-01", workstream: "HR", projectId: "1", projectName: "Tech Corp Acquisition" },
  { id: "5", title: "IT infrastructure audit", description: "Assess current systems", status: "todo", priority: "low", assignee: "Lisa Andersson", dueDate: "2025-02-05", workstream: "IT", projectId: "1", projectName: "Tech Corp Acquisition" },
  { id: "6", title: "Customer analysis", description: "Top 20 customers deep dive", status: "review", priority: "high", assignee: "Anna Lindqvist", dueDate: "2025-01-18", workstream: "Commercial", projectId: "1", projectName: "Tech Corp Acquisition" },
  
  // Project 2 - Global Logistics
  { id: "7", title: "Fleet valuation", description: "Assess vehicle fleet value", status: "in_progress", priority: "high", assignee: "Oscar Nilsson", dueDate: "2025-01-22", workstream: "Financial", projectId: "2", projectName: "Global Logistics Merger" },
  { id: "8", title: "Route optimization analysis", description: "Review delivery routes", status: "todo", priority: "medium", assignee: "Emma Bergström", dueDate: "2025-02-10", workstream: "Operations", projectId: "2", projectName: "Global Logistics Merger" },
  { id: "9", title: "Environmental compliance", description: "ESG standards review", status: "todo", priority: "low", assignee: "Gustav Eriksson", dueDate: "2025-02-15", workstream: "ESG", projectId: "2", projectName: "Global Logistics Merger" },
  { id: "10", title: "Labor union agreements", description: "Review collective agreements", status: "review", priority: "high", assignee: "Maria Svensson", dueDate: "2025-01-28", workstream: "Legal", projectId: "2", projectName: "Global Logistics Merger" },
  
  // Project 3 - Nordic Health
  { id: "11", title: "Patent portfolio review", description: "Assess IP assets", status: "in_progress", priority: "high", assignee: "Johan Karlsson", dueDate: "2025-01-30", workstream: "IP", projectId: "3", projectName: "Nordic Health Acquisition" },
  { id: "12", title: "Clinical trial status", description: "Review ongoing trials", status: "todo", priority: "high", assignee: "Lisa Andersson", dueDate: "2025-02-05", workstream: "Operations", projectId: "3", projectName: "Nordic Health Acquisition" },
  { id: "13", title: "Regulatory compliance", description: "FDA/EMA compliance check", status: "review", priority: "high", assignee: "Erik Johansson", dueDate: "2025-01-25", workstream: "Legal", projectId: "3", projectName: "Nordic Health Acquisition" },
  { id: "14", title: "R&D investment analysis", description: "Future investment needs", status: "done", priority: "medium", assignee: "Anna Lindqvist", dueDate: "2025-01-10", workstream: "Financial", projectId: "3", projectName: "Nordic Health Acquisition" },
];

// Load tasks from localStorage or return initial data
const loadTasks = (): Task[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load tasks from localStorage:', error);
  }
  return initialTasks;
};

// Save tasks to localStorage
const saveTasks = (tasks: Task[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  } catch (error) {
    console.error('Failed to save tasks to localStorage:', error);
  }
};

/**
 * Fetch tasks from localStorage (simulating API)
 */
const fetchTasks = async (projectId?: string): Promise<Task[]> => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 100));
  
  const allTasks = loadTasks();
  
  if (projectId) {
    return allTasks.filter((task) => task.projectId === projectId);
  }
  
  return allTasks;
};

/**
 * Hook to fetch all tasks or tasks for a specific project
 */
export const useTasksQuery = (projectId?: string) => {
  return useQuery({
    queryKey: taskKeys.list(projectId),
    queryFn: () => fetchTasks(projectId),
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to update a task
 */
export const useUpdateTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, updates }: { taskId: string; updates: Partial<Task> }) => {
      const allTasks = loadTasks();
      const updatedTasks = allTasks.map((task) =>
        task.id === taskId ? { ...task, ...updates } : task
      );
      saveTasks(updatedTasks);
      return updatedTasks.find((t) => t.id === taskId)!;
    },
    onSuccess: () => {
      // Invalidate all task queries
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
};

/**
 * Hook to add a new task
 */
export const useAddTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (task: Omit<Task, 'id'>) => {
      const allTasks = loadTasks();
      const newTask: Task = {
        ...task,
        id: Date.now().toString(),
      };
      const updatedTasks = [...allTasks, newTask];
      saveTasks(updatedTasks);
      return newTask;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
};

/**
 * Hook to delete a task
 */
export const useDeleteTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      const allTasks = loadTasks();
      const updatedTasks = allTasks.filter((task) => task.id !== taskId);
      saveTasks(updatedTasks);
      return taskId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
};

// Helper to get project name mapping
export const projectNames: Record<string, string> = {
  "1": "Tech Corp Acquisition",
  "2": "Global Logistics Merger",
  "3": "Nordic Health Acquisition",
};
